import { hexToOklch } from "@/lib/color";

function fmt(l: number, c: number, h: number): string {
	return `oklch(${l.toFixed(6)} ${c.toFixed(6)} ${h.toFixed(3)})`;
}

function clamp(v: number, min = 0, max = 1): number {
	return Math.max(min, Math.min(max, v));
}

/**
 * WCAG-aware foreground: white below L=0.62, near-black above.
 * Threshold 0.62 accounts for mid-range brand colors that
 * need white text for 4.5:1 contrast (e.g. #2563EB blue).
 */
function fg(l: number, h: number): string {
	return l < 0.62 ? fmt(1, 0, 0) : fmt(0.12, 0.02, h);
}

// ─── Neutral fallbacks (no brand color set) ───────────────────────────────────

function neutralPrimary(): Record<string, string> {
	return {
		// Sidebar: dark neutral when no primary color set
		"--sidebar": fmt(0.22, 0, 0),
		"--sidebar-foreground": fmt(1, 0, 0),
		"--sidebar-border": fmt(0.18, 0, 0),
		"--sidebar-ring": fmt(0.4, 0, 0),
		"--sidebar-hover": fmt(0.3, 0, 0),
		"--sidebar-hover-foreground": fmt(1, 0, 0),
		"--sidebar-primary": fmt(0.22, 0, 0),
		"--sidebar-primary-foreground": fmt(1, 0, 0),
		"--sidebar-accent": fmt(0.38, 0, 0),
		"--sidebar-accent-foreground": fmt(1, 0, 0),
		// Charts
		"--chart-1": fmt(0.4, 0, 0),
		"--chart-4": fmt(0.32, 0, 0),
	};
}

function neutralSecondary(): Record<string, string> {
	return {
		// CTA buttons (--primary) fall back to neutral gray.
		// --primary-foreground is auto-derived from --primary via OKLCH
		// relative color syntax in index.css (contrast-aware).
		"--primary": fmt(0.4, 0, 0),
		"--ring": fmt(0.4, 0, 0),
		// Semantic surface tokens
		"--muted": fmt(0.95, 0, 0),
		"--muted-foreground": fmt(0.5, 0, 0),
		"--border": fmt(0.88, 0, 0),
		"--input": fmt(0.88, 0, 0),
		// Secondary / accent
		"--secondary": fmt(0.75, 0, 0),
		"--secondary-foreground": fmt(0.15, 0, 0),
		"--accent": fmt(0.75, 0, 0),
		"--accent-foreground": fmt(0.15, 0, 0),
		// Charts
		"--chart-2": fmt(0.75, 0, 0),
		"--chart-3": fmt(0.85, 0, 0),
		"--chart-5": fmt(0.65, 0, 0),
	};
}

// ─── Brand color derivation ───────────────────────────────────────────────────

function varsFromPrimary(hex: string): Record<string, string> {
	const color = hexToOklch(hex);
	if (!color) return neutralPrimary();

	const { l, c, h } = color;
	const sidebarFg = fg(l, h);

	// Sidebar: use raw hex for exact color match with the picker.
	// Hover/active: lighter and softer (user sees the hierarchy via brightness, not darkness).
	const isDark = l < 0.62;
	const hoverBg = isDark ? "oklch(1 0 0 / 12%)" : "oklch(1 0 0 / 15%)";
	const hoverFg = fg(l, h);
	const accentBg = isDark ? "oklch(1 0 0 / 20%)" : "oklch(1 0 0 / 25%)";
	const accentFg = fg(l, h);
	const borderColor = isDark ? "oklch(1 0 0 / 8%)" : "oklch(0 0 0 / 10%)";

	return {
		"--sidebar": hex,
		"--sidebar-foreground": sidebarFg,
		"--sidebar-border": borderColor,
		"--sidebar-ring": hex,
		"--sidebar-hover": hoverBg,
		"--sidebar-hover-foreground": hoverFg,
		"--sidebar-primary": hex,
		"--sidebar-primary-foreground": sidebarFg,
		"--sidebar-accent": accentBg,
		"--sidebar-accent-foreground": accentFg,
		// Charts
		"--chart-1": hex,
		"--chart-4": fmt(clamp(l - 0.1), clamp(c * 0.9), h),
	};
}

function varsFromSecondary(hex: string): Record<string, string> {
	const color = hexToOklch(hex);
	if (!color) return neutralSecondary();

	const { l, c, h } = color;
	const foreground = fg(l, h);

	// Muted/border: very low chroma tint — safe from gamut clipping
	const mutedBg = fmt(0.96, clamp(c * 0.06, 0, 0.02), h);
	const mutedFg = fmt(0.5, clamp(c * 0.04, 0, 0.015), h);
	const borderVal = fmt(0.88, clamp(c * 0.08, 0, 0.025), h);

	return {
		// Secondary color drives all CTA buttons via --primary.
		// --primary-foreground is auto-derived from --primary via OKLCH
		// relative color syntax in index.css (contrast-aware).
		"--primary": hex,
		"--ring": hex,
		// Semantic surface tokens (secondary-tinted for cohesion)
		"--muted": mutedBg,
		"--muted-foreground": mutedFg,
		"--border": borderVal,
		"--input": borderVal,
		// Secondary / accent
		"--secondary": hex,
		"--secondary-foreground": foreground,
		"--accent": hex,
		"--accent-foreground": foreground,
		// Charts
		"--chart-2": hex,
		"--chart-3": fmt(clamp(l + 0.1), clamp(c * 0.85), h),
		"--chart-5": fmt(clamp(l - 0.12), clamp(c * 0.9), h),
	};
}

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Derives all theme CSS variable values from two brand hex colors.
 *
 * Primary color  → sidebar background (full brand color; hover/active are lighter).
 * Secondary color → CTA buttons (--primary), ring, muted surfaces, border, charts.
 * Falls back to neutral grays when a color is absent or invalid.
 */
export function buildThemeVars(
	primary?: string | null,
	secondary?: string | null,
): Record<string, string> {
	return {
		...(primary ? varsFromPrimary(primary) : neutralPrimary()),
		...(secondary ? varsFromSecondary(secondary) : neutralSecondary()),
	};
}
